
import { Injectable, OnDestroy } from '@angular/core';

import { filter, catchError, map, switchMap, tap, take } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subscription, of, forkJoin, iif, throwError, shareReplay, merge } from 'rxjs';
import { MessageService } from 'primeng/api';

import { MentionsApi } from '../../../api/mentions/mentions.api';
import { CommonApiErrorEnum } from '../../../api/genericApi/enums/common-api-error.enum';
import { MentionsApiErrorEnum } from '../../../api/mentions/enum/mentions-api-error.enum';

import { UserPreferencesService } from '../../../Account/UserPreferences/services/user-preferences.service';

import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { AuditableModel } from '../../../Common/Misc/models/auditable.model';
import { TagModel } from '../../../Common/Tags/models/tag.model';
import { ConfirmDialogModel } from '../../../Common/Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';

import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';

import { WebsocketAwareNeptuneService } from '../../../Bootstrap/services/websocket-aware-neptune.service';

import { BulkMentionRequestModel } from '../../Shared/models/bulk-mention-request-model';
import { MonitoringBulkActionSourceMethodEnum } from '../../Shared/enums/monitoring-bulk-action-source-method.enum';
import { BulkMentionActionModel } from '../../Shared/models/bulk-mention-action.model';

import { Mention } from '../../../Common/Mentions/models/mention';
import { MonitoringRouteModel } from '../../Shared/models/monitoring-route.model';
import { RouteType } from '../../Shared/enums/route-type.enum';
import { Folder } from '../../Shared/models/folder.model';
import { SaveMentionsModel } from '../../Shared/models/save-mentions.model';
import { MentionSearchModel } from '../../Shared/models/mention-search.model';
import { MentionSearchCriteriaModel } from '../../Shared/models/mention-search-criteria.model';

import { SearchResultsService } from './search-results.service';
import { MentionsService } from './mentions.service';
import { MonitoringRoutingService } from './monitoring-routing.service';
import { MentionSearchService } from './mention-search.service';
import { MonitoringBulkActionTypeEnum } from '../../Shared/enums/monitoring-bulk-action-type.enum';
import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';
import { ActionsEnum } from '../../../Common/Basic/enums/actions.enum';
import { NewResourceDialogModel } from '../../../Common/Dialogs/models/new-resource-dialog.model';
import { NewResourceDialogComponent } from '../../../Common/Dialogs/components/NewResourceDialog/new-resource-dialog.component';
import { DisplayGroupModel } from '../../../Common/Layout/models/display-group.model';
import { MultiBaseAbstractApi } from '../../../api/genericApi/multi-base-abstract.api';
import { FoldersApiService } from '@api/folders/folders.api.service';
import { FolderApi } from '@api/folders/models/folder.api.model';
import { CurrentResourceAbstractService } from '@Common/Basic/service/current-resource-abstract.service';
import { ResourceActionTypes } from '@Common/Basic/enums/resource-actions.enum';
import { ResourceAction } from '@Common/Basic/service/resource-abstract.service';
import { gateCall } from '@Core/rxjs-utils';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { DisplayGroupsService } from '@Common/Layout/services/display-groups.service';


/**
 * Used by the MonitoringRouter to determine when the route mode changes
 */
enum FolderMode {
    folders,
    other
}

/**
 * Service for managing folders that contain mentions
 *
 * Behavioral Anomalies
 * - The folders.api.service doesn't have a getById() endpoint
 * - There is not a List model for Folder
 */
@Injectable()
export class FolderService extends CurrentResourceAbstractService<Folder, FolderApi, Folder, FolderApi>
    implements OnDestroy {

    subscriptions: Subscription[] = [];

    all$: Observable<Folder[]> = gateCall(this.currentUserStateService.hasMonitoringAccess()).pipe(
        switchMap(() => merge(
            this.getApiService().getList().pipe(
                map(list => list.map(item => this.constructListModel(item))),
            ),
            this.refreshAllManualSubject,
        )),
        shareReplay(1)
    );

    private newFolderId = new BehaviorSubject<number>(null);
    newFolderId$: Observable<number> = this.newFolderId.asObservable();

    private mentions: Mention[] = null;
    private currentRoute: MonitoringRouteModel;

    readonly FOLDER_DELETION_MENTION_LIMIT = 10000;

    constructor(
        private messageService: MessageService,
        private mentionsApi: MentionsApi,
        private foldersApiService: FoldersApiService,
        private mentionsService: MentionsService,
        private monitoringRoutingService: MonitoringRoutingService,
        private mentionSearchService: MentionSearchService,
        public localizationService: LocalizationService,
        public coreDialogService: CoreDialogService,
        private searchResultsService: SearchResultsService,
        private websocketEvents: WebsocketAwareNeptuneService,
        private userPreferencesService: UserPreferencesService,
        private currentUserStateService: CurrentUserStateService,
        private displayGroupsService: DisplayGroupsService
    ) {
        super();

        this.subscriptions.push(
            this.monitoringRoutingService.getCurrentRoute().pipe(
                filter(route => !!route),
                switchMap((route) => {
                    this.currentRoute = route;

                    const id: number = +this.currentRoute.value;
                    const type: RouteType = this.currentRoute.type;
                    const criteria = new MentionSearchCriteriaModel(undefined, true);

                    if (type === RouteType.mentions ||
                        type === RouteType.allSavedCoverageAnalyticsDashboard ||
                        type === RouteType.allSavedCoverageAnalyticsSyndicates
                    ) {
                        this.setCurrentResource(null);
                        criteria.query.dateQuery = null;
                        return of(criteria);

                    } else if (type === RouteType.folder ||
                        type === RouteType.folderAnalyticsDashboard ||
                        type === RouteType.folderAnalyticsSyndicates
                    ) {
                        if (!id) {
                            this.monitoringRoutingService.gotoSavedCoverageHype();
                            return of(null);
                        } else {
                            return this.all$.pipe(
                                take(1),
                                map(folders => {
                                    if (folders) {
                                        const folder = folders.find(x => x.id === id);
                                        if (!folder) {
                                            this.monitoringRoutingService.gotoSavedCoverageHype({
                                                queryParams: { notFound: 'true' }
                                            });
                                            return;
                                        }
                                        this.setCurrentResource(folder);
                                    } else {
                                        this.setCurrentResource(null);
                                    }

                                    criteria.query.folder.include = [id];
                                    criteria.query.dateQuery = null;
                                    return criteria;
                                }),
                            );
                        }
                    }

                    return of(null);
                }),
                filter(criteria => !!criteria),
                tap((criteria) => {
                    const isFiltersLocked = this.searchResultsService.filtersLocked();
                    const isViewModeChanged = this.searchResultsService.getIsViewModeChange();

                    if ((this.currentRoute.type === RouteType.topicAnalyticsSyndicates) ||
                        (this.currentRoute.type === RouteType.adHocAnalyticsSyndicates) ||
                        (this.currentRoute.type === RouteType.allSavedCoverageAnalyticsSyndicates) ||
                        (this.currentRoute.type === RouteType.folderAnalyticsSyndicates) ||
                        (this.currentRoute.type === RouteType.subscriptionAnalyticsSyndicates)
                    ) {
                        this.mentionSearchService.setShowSyndicates(false);
                    } else {
                        this.mentionSearchService.setShowSyndicates(true);
                    }

                    this.mentionSearchService.setSearchCriteria(criteria);
                    if (!isFiltersLocked) {
                        const filtersQueryString = this.currentRoute.queryParamMap?.get('filters');
                        if (filtersQueryString) {
                            // use date query from filter of route params
                            const dateQuery = MentionSearchCriteriaModel.fromFilterQueryString(filtersQueryString)?.search.query.dateQuery;
                            if (dateQuery) {
                                this.mentionSearchService.setDateQuery(dateQuery);
                            }
                        } else if (!isViewModeChanged) {
                            this.mentionSearchService.setDateQuery(DateQueryModel.getAllQuery());
                            this.mentionSearchService.clearFilters();
                        }
                    } else {
                        this.searchResultsService.loadLockedFiltersFromSession();
                    }
                    this.mentionSearchService.clearSearch(!(isViewModeChanged || isFiltersLocked));
                    this.mentionSearchService.search(true, true);
                })
            ).subscribe(),
            this.mentionsService.getMentions().subscribe(mentions => {
                this.mentions = mentions;
            }),
            // On first load we want to set the page size for the search criteria
            this.monitoringRoutingService.getModeChange(this.currentMode$()).pipe(
                filter(mode => mode.isModeChange),
                switchMap(() => this.userPreferencesService.getUserAppPreferences().pipe(
                    map(userAppPrefs => userAppPrefs.monitoringPrefs.savedCoveragePrefs.defaultDisplaySettings),
                    tap(displayPrefs => {
                        if (!this.mentionSearchService.showSyndicates && (displayPrefs.pageSize > 100)) {
                            this.mentionSearchService.setPageSize(100);
                        } else {
                            this.mentionSearchService.setPageSize(displayPrefs.pageSize);
                        }
                    })
                ))
            ).subscribe(),
            this.websocketEvents.getWebsocketMessages().pipe(
                filter(x => !!x))
                .subscribe(
                    message => {
                        if (message.slug?.startsWith('/mentions/folders')) {
                            return this.refreshAllManual().pipe(
                                filter(x => !!x),
                                take(1),
                            );
                        }
                    }
                )
        );
    }

    getApiService(): MultiBaseAbstractApi<FolderApi, FolderApi> {
        return this.foldersApiService;
    }

    constructResourceModel(apiModel: FolderApi): Folder {
        return new Folder(apiModel);
    }

    constructListModel(apiModel: FolderApi): Folder {
        return new Folder(apiModel);
    }

    messageCenter(actionResult: ResourceAction) {
        const actionType = actionResult.actionType;
        const error = actionResult.error;
        const resource = actionResult.resource as Folder;

        switch(actionType) {
            case ResourceActionTypes.CREATE: {
                if (error) {
                    if (error?.error?.details?.length && error.error.details[0].code === CommonApiErrorEnum.duplicateName) {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: 'MentionFoldersService.folderCreateErrorHeading',
                            detail: 'MentionFoldersService.folderCreateErrorDuplicateNameMessage'
                        });
                    } else {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: 'MentionFoldersService.folderCreateErrorHeading',
                            detail: 'MentionFoldersService.folderCreateErrorGenericMessage'
                        });
                    }
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'MentionFoldersService.folderCreatedHeading',
                        detail: this.localizationService.transform('MentionFoldersService.folderCreatedMessage', {
                            name: resource.name
                        })
                    });
                }

                break;
            }
            case ResourceActionTypes.RENAME: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'MentionFoldersService.renameFolderErrorHeading',
                        detail: 'MentionFoldersService.renameFolderErrorMessage'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'MentionFoldersService.renameFolderSuccessHeading',
                        detail: this.localizationService.transform('MentionFoldersService.renameFolderSuccessMessage', {
                            name: resource.name
                        })
                    });
                }

                break;
            }
            case ResourceActionTypes.DELETE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'MentionFoldersService.deleteFolderErrorHeading',
                        detail: this.localizationService.transform('MentionFoldersService.deleteFolderErrorMessage', {
                            name: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'MentionFoldersService.deleteFolderSuccessHeading',
                        detail: this.localizationService.transform('MentionFoldersService.deleteFolderSuccessMessage', {
                            name: resource.name
                        })
                    });
                }

                break;
            }
            case ResourceActionTypes.BULK_DELETE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'MentionFoldersService.bulkDeleteFolderErrorHeading',
                        detail: 'MentionFoldersService.bulkDeleteFolderErrorMessage'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'MentionFoldersService.bulkDeleteFolderSuccessHeading',
                        detail: this.localizationService.transform('MentionFoldersService.bulkDeleteFolderSuccessMessage', {
                            number: actionResult.count
                        })
                    });
                }

                break;
            }
            case ResourceActionTypes.BULK_VISIBILITY: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'MentionFoldersService.updateFolderErrorHeading',
                        detail: 'MentionFoldersService.updateFolderErrorMessage'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'MentionFoldersService.updateFolderSuccessHeading',
                        detail: this.localizationService.transform('MentionFoldersService.updateFolderSuccessMessage', {
                            number: actionResult.count
                        })
                    });
                }

                break;
            }
            case ResourceActionTypes.COPY: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Monitoring.copyFolder',
                        detail: 'Discover.confirmDialog.copy.error'
                    });
                } else {
                    this.messageService.add({
                        severity : messageSeverityEnum.success,
                        summary: 'Monitoring.copyFolder',
                        detail: 'Discover.confirmDialog.copy.success'
                    });
                }

                break;
            }
        }
    }

    override getById(id: number) {
        throwError(() => new Error('Not implemented'));
        return of(new Folder());
    }

    create(
        folder: Folder,
        group?: DisplayGroupModel,
        refreshResource: boolean = false,
        actionType: ResourceActionTypes = ResourceActionTypes.CREATE,
        refreshAll: boolean = true,
    ): Observable<number> {
        return this.createResource(folder, {
            actionType: actionType,
            refreshResource: refreshResource,
        }).pipe(
            switchMap((id) => {
                if (refreshAll) {
                    return this.refreshAllManual().pipe(
                        filter(x => !!x),
                        take(1),
                        map(x => id)
                    );
                } else {
                    return of(id);
                }
            }),
            switchMap((id) => {
                if (group) {
                    return this.updateGroup([id], group).pipe(
                        map(x => id)
                    );
                } else {
                    return of(id);
                }
            })
        );
    }

    /** Check for duplicates, then rename the specified folder */
    renameListDupCheck(id: number, name: string): Observable<number> {
        return this.isNameDuplicated$(name, id).pipe(
            switchMap((duplicate) => {
                if (duplicate) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'MentionFoldersService.renameFolderErrorHeading',
                        detail: 'MentionFoldersService.renameFolderErrorMessage.duplicate'
                    });
                    throw new Error(CommonApiErrorEnum.duplicateName);
                } else {
                    return this.rename(id, name);
                }
            })
        );
    }

    /**
     * Rename the specified folder (via id) with the new name and force refresh of all$ before returning the id.
     * If the currentResource is the folder being renamed, force a refresh of the currentResource.
     * @param folderId - Folder id that is being renamed
     * @param name - the new updated name property
     */
    rename(folderId: number, name: string): Observable<number> {
        return this.currentResource$.pipe(
            take(1),
            switchMap((currentFolder) => {
                return this.all$.pipe(
                    take(1),
                    map(folders => {
                        const folder = folders.find(x => x.id === folderId);
                        if (folder) {
                            return folder;
                        } else {
                            throwError(() => new Error('Can\'t find folder'));
                        }
                    }),
                    switchMap((folder) => {
                        folder.name = name;
                        return this.updateResource(folder, {
                            actionType: ResourceActionTypes.RENAME,
                            refreshResource: currentFolder?.id === folderId,
                        });
                    }),
                    switchMap((id) => {
                        return this.refreshAllManual().pipe(
                            filter(x => !!x),
                            take(1),
                            map(x => id)
                        );
                    })
                );
            })
        );
    }

    /**
     * Delete the specified folder and force refresh of all$ before returning id.
     * If the currentResource is the folder being deleted, nav to hype screen & reset currentResource to null.
     * @param folder - Folder
     */
    delete(folder: Folder): Observable<number> {
        return this.currentResource$.pipe(
            take(1),
            switchMap((focusedFolder) => {
                return this.deleteResource(folder.id, {
                    actionType: ResourceActionTypes.DELETE,
                    resetResource: focusedFolder?.id === folder.id,
                    resource: folder
                }).pipe(
                    tap(() => {
                        if (focusedFolder?.id === folder.id) {
                            this.monitoringRoutingService.gotoSavedCoverageHype();
                        }

                        if (this.mentions) {
                            // refresh mentions list so we get the mentions without the folder
                            this.mentionsService.refetchMentions(this.mentions.map(x => x.mentionId), false);
                        }
                    })
                );
            }),
            switchMap((folderId) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => folderId)
                );
            }),
        );
    }

    private bulkDelete(ids: number[]): Observable<number[]> {
        return this.currentResource$.pipe(
            take(1),
            switchMap((focusedFolder) => {
                const action = {
                    actionType: ResourceActionTypes.BULK_DELETE,
                    resetResource: ids.includes(focusedFolder?.id),
                    count: ids.length
                };

                return this.bulkDeleteResourceByIds(ids, action).pipe(
                    tap((deleteIds) => {
                        if (ids.includes(focusedFolder?.id)) {
                            this.monitoringRoutingService.gotoSavedCoverageHype();
                        }
                    })
                );
            }),
            switchMap((id) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => id)
                );
            })
        );
    }


    /**
     * Since we can only delete folders that contain less than 10,000 mentions and deletes are done individually, invalid folders are filtered out.
     * @TODO revisit behaviour with BE bulk action endpoints are implemented
     */

    /**
     * Similar logic is used across different services.
     *
     * @TODO Consider consolidating repeated bulk deletion logic using shared CRUD interfaces.
     */
    confirmBulkDelete(items: AuditableModel[], groups: DisplayGroupModel[]): Observable<boolean> {
        const foldersOverLimit = items.filter(item => item['totalMentions'] > this.FOLDER_DELETION_MENTION_LIMIT);
        const list = items.map(item => item.name);
        const secondaryList = groups.map(item => item.name);

        return new Observable<boolean>((subscriber) => {
            let isListSelected: boolean = true;
            let isSecondaryListSelected: boolean = false;

            const confirmDeleteIO: ConfirmDialogModel = {
                inputs: {
                    id: 'FoldersBulkDelete',
                    heading: 'Monitoring.bulkDeleteFolder',
                    alertType: messageSeverityEnum.warning,
                    alertMessage: 'Monitoring.confirmBulkDeleteFolder.alertMessage',
                    message: 'Common.confirmDialog.message',
                    list,
                    listLabel: 'Monitoring.confirmBulkDeleteFolder.checkboxLabel',
                    isListSelected,
                    secondaryList,
                    secondaryListLabel: 'Common.confirmDialog.checkboxLabel.groups',
                    isSecondaryListSelected,
                    okLabel: 'Core.button.delete'
                },
                outputs: {
                    listSelectionChanged: (isSelected: boolean) => {
                        isListSelected = isSelected;
                    },
                    secondaryListSelectionChanged: (isSelected: boolean) => {
                        isSecondaryListSelected = isSelected;
                    },
                    dialogClose: (confirmed: any) => {
                        if (confirmed) {
                            if (isListSelected) {
                                const ids = items.map(item => item.id);
                                this.bulkDelete(ids).subscribe();
                            }

                            if (isSecondaryListSelected) {
                                groups.forEach(group => this.displayGroupsService.deleteGroup(group));
                            }
                        }
                        subscriber.next(confirmed);
                        subscriber.complete();
                    }
                }
            };

            if (foldersOverLimit.length > 0) {
                const overLimitNames = foldersOverLimit.map(item => {
                    return item.name;
                });
                const overLimitWarningIO: ConfirmDialogModel = {
                    inputs: {
                        heading: 'MentionFoldersService.bulkDeleteFolderExceededLimitErrorHeading',
                        messageHtml: 'MentionFoldersService.bulkDeleteFolderExceededLimitErrorMessageHtml',
                        alertMessage: 'MentionFoldersService.bulkDeleteFolderExceededLimitErrorAlertMessage',
                        bottomMessage: 'MentionFoldersService.bulkDeleteFolderExceededLimitErrorBottomMessage',
                        alertType: messageSeverityEnum.error,
                        list: overLimitNames,
                        okLabel: 'continue',
                        id: 'FoldersOverLimitWarning',
                        showCancel: true
                    },
                    outputs: {
                        dialogClose: (confirmed: boolean) => {
                            if (confirmed) {
                                this.coreDialogService.open(ConfirmDialogComponent, confirmDeleteIO);
                            } else {
                                subscriber.next(false);
                                subscriber.complete();
                            }
                        }
                    }
                };
                this.coreDialogService.open(ConfirmDialogComponent, overLimitWarningIO);
            } else {
                this.coreDialogService.open(ConfirmDialogComponent, confirmDeleteIO);
            }
        });
    }

    /** Update the hidden state of the specified folder (via id) and force refresh of all$ before returning id. */
    updateVisibility(folderId: number, action: ActionsEnum): Observable<number> {
        return this.all$.pipe(
            take(1),
            map(folders => {
                const folder = folders.find(x => x.id === folderId);
                if (folder) {
                    return folder;
                } else {
                    throwError(() => new Error('Can\'t find folder'));
                }
            }),
            switchMap(folder => {
                folder.hidden = action === ActionsEnum.hide;
                return this.updateResource(folder, { actionType: ResourceActionTypes.VISIBILITY }, false);
            }),
            switchMap((id) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => id)
                );
            })
        );
    }

    /**
     * Update the hidden state of the specified folders and force refresh of all$ before returning subscription ids.
     */
    bulkUpdateVisibility(ids: number[], action: ActionsEnum): Observable<number[]> {
        return this.all$.pipe(
            take(1),
            map(folders => {
                return folders.filter(folder => ids.includes(folder.id));
            }),
            switchMap((folders: Folder[]) => {
                folders.forEach(folder => folder.hidden = action === ActionsEnum.hide);
                return this.updateBulkResources(folders);
            }),
            switchMap((folderIds) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => folderIds)
                );
            })
        );
    }

    updateFavorites(addIds: number[], removeIds: number[]): Observable<void> {
        return this.foldersApiService.updateFavorites(addIds, removeIds).pipe(
            catchError((error) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Common.lhp.moveToGroup.error.summary',
                    detail: 'Common.lhp.moveToGroup.error.detail'
                });

                throw new Error(error);
            }),
            switchMap(() => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => null)
                );
            }),
        );
    }


    openCopyFolderDialog(folder: Folder) {
        const io: NewResourceDialogModel = {
            inputs: {
                heading: 'Monitoring.copyFolder',
                initialValue: folder.name + this.localizationService.transform('Common.dialog.newResource.copy.suffix'),
                currentGroup: folder.groupId,
                okLabel: 'Common.dialog.newResource.copy',
                observableFormValidator: (folderName: string) => {
                    return this.isNameDuplicated$(folderName).pipe(
                        map((isDuplicate) => {
                            if (isDuplicate) {
                                return ['Monitoring.result.newFolder.error.duplicate'];
                            } else {
                                return [];
                            }
                        })
                    );
                },
                submit: (name: string, group?: DisplayGroupModel) => {
                    const copyFolder = folder.clone();
                    copyFolder.name = name;

                    if (group) {
                        copyFolder.groupId = group.id;
                    }

                    return this.create(copyFolder, group, false, ResourceActionTypes.COPY, false).pipe(
                        switchMap(id => {
                            const modifications = new BulkMentionActionModel();
                            modifications.addFolder = id;
                            modifications.currentActionType = MonitoringBulkActionTypeEnum.addFolder;

                            const searchModel = new MentionSearchCriteriaModel();
                            searchModel.query.folder.include = [folder.id];
                            searchModel.isAccountSearch = true;
                            searchModel.query.keywordGroups = [];
                            searchModel.query.dateQuery.type = DateQueryTypeEnum.ALL;

                            const bulkMentionRequest = new BulkMentionRequestModel(
                                MonitoringBulkActionSourceMethodEnum.SEARCH,
                                modifications,
                                searchModel,
                                [],
                                false
                            );

                            return this.mentionsApi.updateMentionsInBulk(bulkMentionRequest.toApi()).pipe(
                                map(x => id),
                            );
                        }),
                        switchMap(id => {
                            return this.refreshAllManual().pipe(
                                map(x => id)
                            );
                        }),
                        tap((id) => this.monitoringRoutingService.gotoFolder(id)),
                    );
                }
            }
        };
        this.coreDialogService.open(NewResourceDialogComponent, io);
    }

    openNewFolderDialog() {
        const io: NewResourceDialogModel = {
            inputs: {
                heading: 'Monitoring.result.newFolderDialogTitle',
                description: 'Monitoring.newFolderMessage',
                placeholder: 'Monitoring.enterFolderNameHint',
                okLabel: 'Monitoring.createFolder',
                cancelLabel: 'Monitoring.result.cancelButtonLabel',
                width: 300,
                id: 'createFolder',
                disabledButtonTooltip: 'Monitoring.result.newFolder.disabledTooltip',
                observableFormValidator: (folderName: string) => {
                    return this.isNameDuplicated$(folderName).pipe(
                        map((isDuplicate) => {
                            if (isDuplicate) {
                                return ['Monitoring.result.newFolder.error.duplicate'];
                            } else {
                                return [];
                            }
                        })
                    );
                },
                submit: (folderName: string, group: DisplayGroupModel) => {
                    const folder = new Folder();
                    folder.name = folderName;

                    if (group) {
                        folder.groupId = group.id;
                    }

                    return this.create(folder, group).pipe(
                        tap((id) => {
                            if (id) {
                                this.newFolderId.next(id);
                                this.monitoringRoutingService.gotoFolder(id);
                            }
                        })
                    );
                }
            },
            outputs: {}
        };

        this.coreDialogService.open(NewResourceDialogComponent, io);
    }


    /**
     * Performs a search, returning folders that substring matches the given queryString
     * @param queryString the string to match against the name of the folders
     */
    quicksearchFoldersByName(queryString: string): Observable<Folder[]> {
        const query = queryString.toLocaleLowerCase();
        return this.all$.pipe(
            take(1),
            map(folders => folders.filter(folder => folder.name.toLocaleLowerCase().indexOf(query) !== -1))
        );
    }


    /**
     * Saves the provided mentions to the provided folder
     */
    public saveMentionsToFolder(
        mentions: Mention[],
        folder: Folder | Folder[],
        refetchMentions: boolean = true,
        search?: MentionSearchModel,
        tags?: TagModel[],
        forceSyndicateSave?: boolean
    ): Observable<string[]> {
        const saveMentionsModel = new SaveMentionsModel(mentions, search);
        let  folderName: string;
        if(Array.isArray(folder)) {
            saveMentionsModel.folderIds = folder.map(itm => itm.id);
            folderName = folder.map(itm => itm.name).join(', ');
        } else {
            saveMentionsModel.folderId = folder.id;
            folderName = folder.name;
        }

        if (!saveMentionsModel.mentions || !saveMentionsModel.mentions.length) {
            return;
        }

        return this.mentionsApi.saveMentions(saveMentionsModel.toApiModel(forceSyndicateSave)).pipe(
            map((data) => {
                const mentionIds: string[] = [];
                const allMentionIds: string[] = [];

                for (const idMapping of data) {
                    const mercuryId = idMapping.mercuryId;
                    const mentionId = idMapping.mentionId;
                    const mappedMention = saveMentionsModel.mentions.find((mention) => {
                        if (mention.id) {
                            return mention.id === mercuryId;
                        }
                        if (mention.mentionId) {
                            return mention.mentionId === mentionId;
                        }
                    });
                    if (mappedMention) {
                        mappedMention.mentionId = mentionId;
                        mentionIds.push(mappedMention.mentionId);
                    }
                    allMentionIds.push(mentionId);
                }

                return {allMentionIds, mentionIds};
            }),
            switchMap(({allMentionIds, mentionIds}) => {
                if (tags?.length > 0) {
                    return this.mentionsService.saveTagsToMentions(allMentionIds, tags).pipe(
                        catchError(err => of(null)),
                        map(() => mentionIds) // Always returns mentionIds
                    );
                }
                return of(mentionIds);
            }),
            tap((mentionIds) => {
                if (mentionIds.length > 0) {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'MentionFoldersService.saveMentionsHeading',
                        detail: this.localizationService.transform('MentionFoldersService.saveMentionsToFolder', {
                            count: mentionIds.length, folderName: folderName
                        })
                    });
                }

                if (refetchMentions) {
                    this.mentionsService.refetchMentions(mentionIds, false);
                }
            }),
            switchMap((mentionIds) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => mentionIds)
                );
            }),
            catchError((error) => {
                let hasShownError = false;
                if (error && error.error && error.error.details) {
                    for (const errorDetail of error.error.details) {
                        switch (errorDetail.code) {
                            case MentionsApiErrorEnum.folderLimitReached:
                                this.messageService.add({
                                    severity: messageSeverityEnum.error,
                                    summary: 'MentionFoldersService.saveMentionsFailedHeading',
                                    detail: 'MentionFoldersService.saveMentionsFailedDetail.limitReached'
                                });
                                hasShownError = true;
                                break;
                        }
                    }
                }

                if (!hasShownError && error?.error && error.error.includes('Available quota')) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'MentionFoldersService.saveMentions.reachQuota.heading',
                        detail: 'MentionFoldersService.saveMentions.reachQuota.detail'
                    });

                    hasShownError = true;
                }

                if (!hasShownError) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'MentionFoldersService.saveMentionsFailedHeading',
                        detail: error.detail
                    });
                }

                throw error;
            })
        );
    }

    /**
     * Removes the provided mentions from the provided folder
     */
    removeMentionsFromFolder(mentions: Mention[], folder: Folder): Observable<number> {
        if (!folder || !mentions || mentions.length < 1) {
            return of(null);
        }

        const modifications = new BulkMentionActionModel({ removeFolder: folder.id });
        const mentionIds = mentions.map((mention) => mention.mentionId);

        const body: BulkMentionRequestModel = new BulkMentionRequestModel(
            MonitoringBulkActionSourceMethodEnum.SELECTION,
            modifications,
            null,
            mentionIds
        );

        return this.mentionsApi.updateMentionsInBulk(body.toApi()).pipe(
            catchError((error) => {
                if (error.status === 404) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'MentionFoldersService.removeMentionsFailedHeading',
                        detail: 'MentionFoldersService.removeMentionsFromFolderNotFoundError'
                    });
                } else {
                    // @TODO: Handle other errors?
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'MentionFoldersService.removeMentionsFailedHeading',
                        detail: 'MentionFoldersService.removeMentionsFromFolderGenericError'
                    });
                }

                this.mentionSearchService.search(true, true);

                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                );
            }),
            tap(() => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'MentionFoldersService.removeMentionsHeading',
                    detail: this.localizationService.transform('MentionFoldersService.removeMentionsFromFolder', {
                        count: mentions.length, folderName: folder.name
                    })
                });
            }),
            switchMap(() => this.currentResource$.pipe(take(1))),
            tap(focusedFolder => {
                if (focusedFolder && focusedFolder.id === folder.id) {
                    // refresh the search if it's the current selected folder, since the mentions are removed from it
                    this.mentionSearchService.search(true, true);
                } else {
                    // refresh the mentions, but don't worry about the search, because they weren't removed from the current search
                    this.mentionsService.refetchMentions(mentions.map(mention => mention.mentionId), false);
                }
            }),
            switchMap(() => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => folder.id)
                );
            }),
        );
    }


    /**
     * Adds provided folders to group (or removes group ID if Uncategorized is selected)
     * @param folderIds
     * @param group undefined if Uncategorized is selected
     * @param refreshAll when true triggers a fresh of all$
     */
    updateGroup(folderIds: number[], group: DisplayGroupModel, refreshAll: boolean = false): Observable<Folder> {
        let request: Observable<void>;

        if (group?.id) {
            request = this.foldersApiService.updateDisplayGroup(folderIds, [], group.id);
        } else {
            request = this.foldersApiService.removeDisplayGroup(folderIds);
        }

        return request.pipe(
            catchError(() => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Common.lhp.moveToGroup.error.summary',
                    detail: 'Common.lhp.moveToGroup.error.detail'
                });

                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                );
            }),
            switchMap(() =>
                iif(
                    () => refreshAll,
                    this.refreshAllManual().pipe(
                        filter(x => !!x),
                        take(1),
                    ),
                    of(null)
                )
            ),
            switchMap(() =>
                iif(
                    () => folderIds.length === 1,
                    this.all$.pipe(
                        take(1),
                        map(folders => folders.find(folder => folder.id === folderIds[0])),
                    ),
                    of(null)
                )
            ),
            tap((folder: Folder) => {
                if (folder) {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Common.lhp.moveToGroup.success.summary',
                        detail: this.localizationService.transform('Common.lhp.moveToGroup.success.detail.single',
                            { itemName: folder?.name, groupName: group.name }
                        )
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Common.lhp.moveToGroup.success.summary',
                        detail: this.localizationService.transform('Common.lhp.moveToGroup.success.detail.multiple',
                            { number: folderIds.length, name: group.name }
                        )
                    });
                }
            }),
        );
    }


    /**
     * Removes the provided folder ids from all display groups & refresh all$
     */
    removeFoldersFromGroup(ids: number[]): Observable<null> {
        return this.foldersApiService.removeDisplayGroup(ids).pipe(
            switchMap(() => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => null)
                );
            })
        );
    }


    getFoldersByIds(ids: number[]): Observable<Folder[]> {
        return this.all$.pipe(
            filter(x => !!x),
            map(folders => folders.filter(folder => ids.includes(folder.id))),
            take(1)
        );
    }

    /**
     * Gets all of the folders the provided mention is saved to.
    */
    getFoldersByMention(mention: Mention): Observable<Folder[]> {
        if (!mention || !mention?.folderIds) {
            return of(null);
        }

        return this.all$.pipe(
            filter(x => !!x),
            map(folders => folders.filter(folder => mention.folderIds.includes(folder.id)))
        );
    }



    /**
     * Constructs a unique list of folder names that are associated with the array of mentions.
     */
    getListOfFoldersMentionsFoundIn(mentions: Mention[]): Observable<string[]> {
        const requests: Observable<Folder[]>[] = mentions.map(x => this.getFoldersByMention(x));

        return forkJoin(requests).pipe(
            map(folders => folders.flat(1).map(x => x.name)),
            map(folderNames => [...new Set(folderNames)])
        );
    }



    private currentMode$(): Observable<FolderMode> {
        return this.monitoringRoutingService.getCurrentRoute().pipe(
            map(route => {
                const folderRoutes = [
                    RouteType.mentions,
                    RouteType.folder
                ];
                return (
                    folderRoutes.includes(route.type)
                        ? FolderMode.folders
                        : FolderMode.other
                );
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
